// ./src/hooks/usePlayListItems.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  PlayListItem,
  PlayListItemService,
  PlayListItemFilter,
  PlayListItemInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UsePlayListItemsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<PlayListItemFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;

  userId?: string | null;
}

export interface UsePlayListItemsReturn {
  // Data state
  playListItem: PlayListItem | null;
  playListItems: PlayListItem[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<PlayListItemFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingPlayListItemId: string | null;
  selectedPlayListItem: PlayListItem | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<PlayListItemFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<PlayListItem[]>;
  create: (params: PlayListItemInput) => Promise<PlayListItem | null>;
  update: (
    id: string,
    params: PlayListItemInput
  ) => Promise<PlayListItem | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Search and filter operations
  setFilters: (filters: Partial<PlayListItemFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedPlayListItem: (playListItem: PlayListItem | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  playListItemsByStatus: Record<string, PlayListItem[]>;
  playListItemsByPlayList: Record<string, PlayListItem[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "orderNo", direction: "asc" }];

export const usePlayListItems = (
  options: UsePlayListItemsOptions = {}
): UsePlayListItemsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [playListItem, setPlayListItem] = useState<PlayListItem | null>(null);
  const [playListItems, setPlayListItems] = useState<PlayListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<PlayListItemFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingPlayListItemId, setRemovingPlayListItemId] = useState<
    string | null
  >(null);
  const [selectedPlayListItem, setSelectedPlayListItem] =
    useState<PlayListItem | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => playListItems.length === 0 && !loading,
    [playListItems.length, loading]
  );

  const playListItemsByStatus = useMemo(() => {
    return playListItems.reduce((acc, item) => {
      const status = String(item.status || "unknown");
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(item);
      return acc;
    }, {} as Record<string, PlayListItem[]>);
  }, [playListItems]);

  const playListItemsByPlayList = useMemo(() => {
    return playListItems.reduce((acc, item) => {
      const playListId = item.playListId || "unknown";
      if (!acc[playListId]) {
        acc[playListId] = [];
      }
      acc[playListId].push(item);
      return acc;
    }, {} as Record<string, PlayListItem[]>);
  }, [playListItems]);

  // Helper function to update playlist item in list if it exists, or add it if not
  const updateOrAddPlayListItemInList = useCallback(
    (updatedItem: PlayListItem) => {
      setPlayListItems((prev) => {
        const existingIndex = prev.findIndex(
          (item) => item.id === updatedItem.id
        );
        if (existingIndex >= 0) {
          const newItems = [...prev];
          newItems[existingIndex] = updatedItem;
          return newItems;
        } else {
          return [updatedItem, ...prev];
        }
      });
    },
    []
  );

  // Helper function to remove playlist item from list if it exists
  const removePlayListItemFromList = useCallback((itemId: string) => {
    setPlayListItems((prev) => prev.filter((item) => item.id !== itemId));
  }, []);

  // Helper function to remove multiple playlist items from list
  const removePlayListItemsFromList = useCallback((itemIds: string[]) => {
    const idsSet = new Set(itemIds);
    setPlayListItems((prev) =>
      prev.filter((item) => !idsSet.has(item.id || ""))
    );
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setPlayListItem(null);
    setPlayListItems([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingPlayListItemId(null);
    setSelectedPlayListItem(null);
  }, []);

  // Get single playlist item (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await PlayListItemService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const itemData = result.data[0] as PlayListItem;
          setPlayListItem(itemData);
          updateOrAddPlayListItemInList(itemData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load playlist item";
          setLastError(errorMessage);
          setPlayListItem(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load playlist item";
        setLastError(errorMessage);
        setPlayListItem(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddPlayListItemInList]
  );

  // List playlist items (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<PlayListItemFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setPlayListItems([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await PlayListItemService.list(
          filter as PlayListItemFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setPlayListItems((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(item.id)
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            setPlayListItems(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load playlist items";
          setLastError(errorMessage);
          if (!isAppending) setPlayListItems([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load playlist items";
        setLastError(errorMessage);
        if (!isAppending) setPlayListItems([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many playlist items by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<PlayListItem[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await PlayListItemService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedItems = Array.isArray(result.data) ? result.data : [];
          loadedItems.forEach((item) => {
            updateOrAddPlayListItemInList(item);
          });
          setLastError(null);
          return loadedItems;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load playlist items";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load playlist items";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddPlayListItemInList]
  );

  // Create playlist item (matching API method name)
  const create = useCallback(
    async (params: PlayListItemInput): Promise<PlayListItem | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await PlayListItemService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItem = result.data[0] as PlayListItem;
          setPlayListItem(newItem);
          setPlayListItems((prev) => [...prev, newItem]);
          setLastError(null);
          return newItem;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create playlist item";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create playlist item";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update playlist item (matching API method name)
  const update = useCallback(
    async (
      id: string,
      params: PlayListItemInput
    ): Promise<PlayListItem | null> => {
      if (!id) {
        setLastError("PlayListItem ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await PlayListItemService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = result.data[0] as PlayListItem;
          setPlayListItem(updatedItem);

          // Update item in list with sorting by orderNo
          setPlayListItems((prev) => {
            const existingIndex = prev.findIndex(
              (item) => item.id === updatedItem.id
            );
            if (existingIndex >= 0) {
              const newItems = [...prev];
              newItems[existingIndex] = updatedItem;
              return newItems.sort(
                (a, b) => (a.orderNo ?? 0) - (b.orderNo ?? 0)
              );
            } else {
              return [...prev, updatedItem];
            }
          });

          setLastError(null);
          return updatedItem;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update playlist item";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update playlist item";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    []
  );

  // Remove playlist item (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("PlayListItem ID is required for removal");
        return false;
      }

      setRemovingPlayListItemId(id);
      setLastError(null);

      try {
        const result = await PlayListItemService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setPlayListItem(null);
          removePlayListItemFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove playlist item";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove playlist item";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingPlayListItemId(null);
      }
    },
    [removePlayListItemFromList]
  );

  // Remove many playlist items (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError("At least one PlayListItem ID is required for removal");
        return false;
      }

      setLastError(null);

      try {
        const result = await PlayListItemService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setPlayListItem(null);
          removePlayListItemsFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove playlist items";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove playlist items";
        setLastError(errorMessage);
        return false;
      }
    },
    [removePlayListItemsFromList]
  );

  // Load more playlist items
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh playlist items
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && isAuthenticated && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, isAuthenticated, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    playListItem,
    playListItems,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingPlayListItemId,
    selectedPlayListItem,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedPlayListItem,
    reset,
    playListItemsByStatus,
    playListItemsByPlayList,
    lastError,
    clearError,
  };
};
